/* SPDX-FileCopyrightText: 2014-present Kriasoft */
/* SPDX-License-Identifier: MIT */

/**
 * Persists optional enterprise attribution from the URL so sign-in requests
 * (Google OAuth callback, email code) can attach enterprise_canonical_id and
 * optional additional_info.
 *
 * Partners can deep-link with ?enterprise_source=<json payload>
 * where payload includes { source, user_token, ts, exp }.
 */
const STORAGE_KEY = "povchat_enterprise_canonical_id_v1";
const STORAGE_INFO_KEY = "povchat_enterprise_additional_info_v1";
const STORAGE_SEEN_KEY = "povchat_enterprise_source_seen_v1";

type EnterpriseSourcePayload = {
  source?: string;
  user_token?: string;
};

function parseEnterpriseSource(raw: string): {
  enterpriseCanonicalId: string;
  additionalInfo?: string;
} | null {
  const value = raw.trim();
  if (!value) return null;

  try {
    const parsed = JSON.parse(value) as EnterpriseSourcePayload;
    const source = parsed?.source?.trim();
    if (!source) return null;

    const userToken = parsed?.user_token?.trim();
    const additionalInfo = userToken ? `user_token: ${userToken}` : undefined;
    return { enterpriseCanonicalId: source, additionalInfo };
  } catch {
    return null;
  }
}

export function persistEnterpriseSourceFromSearch(search: string): void {
  const params = new URLSearchParams(search);
  const raw = params.get("enterprise_source");
  if (!raw) return;

  sessionStorage.setItem(STORAGE_SEEN_KEY, "1");

  const parsed = parseEnterpriseSource(raw);
  if (!parsed) return;

  sessionStorage.setItem(STORAGE_KEY, parsed.enterpriseCanonicalId);
  if (parsed.additionalInfo) {
    sessionStorage.setItem(STORAGE_INFO_KEY, parsed.additionalInfo);
  } else {
    sessionStorage.removeItem(STORAGE_INFO_KEY);
  }
}

export function hasEnterpriseAuthContext(search = ""): boolean {
  const params = new URLSearchParams(search);
  if (params.has("enterprise_source")) return true;

  try {
    return Boolean(
      sessionStorage.getItem(STORAGE_SEEN_KEY)?.trim() ||
        sessionStorage.getItem(STORAGE_KEY)?.trim(),
    );
  } catch {
    return false;
  }
}

/** JSON fields to merge into auth POST bodies when a canonical id was captured. */
export function enterpriseAuthJsonExtras(): Record<string, string> {
  try {
    const v = sessionStorage.getItem(STORAGE_KEY)?.trim();
    if (v) {
      const additionalInfo = sessionStorage.getItem(STORAGE_INFO_KEY)?.trim();
      return {
        enterprise_canonical_id: v,
        ...(additionalInfo ? { additional_info: additionalInfo } : {}),
      };
    }
  } catch {
    /* sessionStorage unavailable */
  }
  return {};
}
